import {
  DOCS_FORM_NAMES,
  LACK_OF_CAPACITY_DOCUMENT,
  LEGAL_GUARDIAN,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  REQUESTED_REHAB_SERVICE,
  REQUESTED_REHAB_SERVICE_TYPE,
} from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntologyRadioOptions } from "core/model/utils/ontologies/hooks";
import { SEND_DOCUMENTS_CHARACTER_LIMIT } from "core/schemas/shared";
import { DRVGeneralFormData } from "core/types";
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledInput,
  ControlledRadioGroup,
  ControlledTextarea,
} from "ds/ui";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormFieldConfigProps } from "..";
import { SourceWrapper } from "../../utils";

export const MaritalStatusField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const options = useGetOntologyRadioOptions({
    type: ONTOLOGIES.MARITAL_STATUS,
  });

  return (
    <ControlledRadioGroup
      aria-label={
        translations.patientForms.drvGeneralForm.sectionThree.subtitle
      }
      name="marital_status"
      options={options}
      size="md"
    />
  );
};

export const EmploymentStatusField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const options = useGetOntologyRadioOptions({
    type: ONTOLOGIES.EMPLOYMENT_STATUS,
  });

  return (
    <ControlledRadioGroup
      label={translations.patientForms.drvGeneralForm.sectionFour.subtitle}
      name="employment_status"
      options={options}
      size="md"
    />
  );
};

export const PriorEmploymentStatusField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const options = useGetOntologyRadioOptions({
    type: ONTOLOGIES.PRIOR_EMPLOYMENT_STATUS,
  });

  return (
    <ControlledRadioGroup
      aria-label={translations.patientForms.drvGeneralForm.sectionFive.title}
      name="prior_employment_status"
      options={options}
      size="md"
    />
  );
};

export const InsuranceTypeField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const options = useGetOntologyRadioOptions({
    type: ONTOLOGIES.INSURANCE_TYPE,
  });

  return (
    <ControlledRadioGroup
      label={
        translations.patientForms.drvGeneralForm.sectionSix.patientInsurance
      }
      name="insurance_type"
      options={options}
      size="md"
    />
  );
};

export const RequestedRehabServiceTypeInsuredField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const options = [
    {
      label: translations.patientForms.drvGeneralForm.sectionOne.outpatient,
      value: REQUESTED_REHAB_SERVICE_TYPE.OUTPATIENT,
    },
    {
      label: translations.patientForms.drvGeneralForm.sectionOne.inpatient,
      value: REQUESTED_REHAB_SERVICE_TYPE.INPATIENT,
    },
  ];

  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
      fieldName="requested_rehab_service_type_insured"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledRadioGroup
        label={translations.patientForms.medicalForm.sectionSix.taxiOptionLabel}
        name="requested_rehab_service_type_insured"
        options={options}
        size="md"
      />
    </SourceWrapper>
  );
};

export const RequestedRehabServiceTypeRelativesField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const options = [
    {
      label: translations.patientForms.drvGeneralForm.sectionOne.outpatient,
      value: REQUESTED_REHAB_SERVICE_TYPE.OUTPATIENT,
    },
    {
      label: translations.patientForms.drvGeneralForm.sectionOne.inpatient,
      value: REQUESTED_REHAB_SERVICE_TYPE.INPATIENT,
    },
  ];

  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
      fieldName="requested_rehab_service_type_relatives"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledRadioGroup
        label={translations.patientForms.medicalForm.sectionSix.taxiOptionLabel}
        name="requested_rehab_service_type_relatives"
        options={options}
        size="md"
      />
    </SourceWrapper>
  );
};

export const PatientUnableToWorkDateField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
      fieldName="patient_unable_to_work_before_hospitalization_date"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledDatePicker
        label={translations.patientForms.drvGeneralForm.sectionSix.sinceDate}
        name="patient_unable_to_work_before_hospitalization_date"
      />
    </SourceWrapper>
  );
};

export const ContributionsToForeignInsuranceInfoFields = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  return (
    <>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="contributions_to_foreign_insurance_info_state"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={
            translations.patientForms.drvGeneralForm.sectionEight
              .contributionsState
          }
          name="contributions_to_foreign_insurance_info_state"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="contributions_to_foreign_insurance_info_from_to"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={translations.patientForms.drvGeneralForm.sectionEight.timeline}
          name="contributions_to_foreign_insurance_info_from_to"
        />
      </SourceWrapper>
    </>
  );
};

export const JobcenterNameField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
      fieldName="jobcenter_name"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledInput
        label={translations.patientForms.drvGeneralForm.sectionNine.jobCenter}
        name="jobcenter_name"
      />
    </SourceWrapper>
  );
};

export const RetirementPensionReceivedField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
      fieldName="retirement_pension_received"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledInput
        label={
          translations.patientForms.drvGeneralForm.sectionTen
            .namePensionInsurance
        }
        name="retirement_pension_received"
      />
    </SourceWrapper>
  );
};

export const BeforeRetirementPensionField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
      fieldName="before_retirement_pension"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledInput
        label={translations.patientForms.drvGeneralForm.sectionTen.benefitsType}
        name="before_retirement_pension"
      />
    </SourceWrapper>
  );
};

export const HealthDamageFields = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  return (
    <>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="health_damage_origin"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={
            translations.patientForms.drvGeneralForm.sectionTen.recognisedBy
          }
          name="health_damage_origin"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="health_damage_file_number"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={translations.patientForms.drvGeneralForm.sectionTen.fileNumber}
          name="health_damage_file_number"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="health_damages_description"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledTextarea
          label={
            translations.patientForms.drvGeneralForm.sectionTen.healthIssues
          }
          name="health_damages_description"
        />
      </SourceWrapper>
    </>
  );
};

export const ApplicationSubmittedField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
      fieldName="application_submitted"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledInput
        label={
          translations.patientForms.drvGeneralForm.sectionTen.submittedWhere
        }
        name="application_submitted"
      />
    </SourceWrapper>
  );
};

export const ClaimForDamagesFields = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  return (
    <>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="claim_for_damages_origin"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={
            translations.patientForms.drvGeneralForm.sectionTen
              .compensationClaims
          }
          name="claim_for_damages_origin"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="claim_for_damages_file_number"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={translations.patientForms.drvGeneralForm.sectionTen.fileNumber}
          name="claim_for_damages_file_number"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="claim_for_damages_description"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledDatePicker
          label={
            translations.patientForms.drvGeneralForm.sectionTen.compensatedOn
          }
          name="claim_for_damages_description"
        />
      </SourceWrapper>
    </>
  );
};

export const ThirdPartyRequestFields = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const legalGuardianOptions = [
    {
      label: translations.patientForms.drvGeneralForm.sectionEleven.legalRep,
      value: LEGAL_GUARDIAN.STATUTORY_REP,
    },
    {
      label: translations.patientForms.drvGeneralForm.sectionEleven.guardian,
      value: LEGAL_GUARDIAN.CUSTODIAN,
    },
    {
      label: translations.patientForms.drvGeneralForm.sectionEleven.carer,
      value: LEGAL_GUARDIAN.SUPERVISOR,
    },
    {
      label:
        translations.patientForms.drvGeneralForm.sectionEleven
          .authorisedRepresentative,
      value: LEGAL_GUARDIAN.AUTHORIZED,
    },
  ];

  const lackOfCapacityOptions = [
    {
      label: translations.patientForms.drvGeneralForm.sectionEleven.attached,
      value: LACK_OF_CAPACITY_DOCUMENT.ATTACHED,
    },
    {
      label:
        translations.patientForms.drvGeneralForm.sectionEleven.submittedLater,
      value: LACK_OF_CAPACITY_DOCUMENT.SEND_LATER,
    },
  ];

  return (
    <>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="contact_guardian"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={translations.patientForms.drvGeneralForm.sectionEleven.nameRep}
          name="contact_guardian"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="legal_guardian_type"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledRadioGroup
          label={
            translations.patientForms.drvGeneralForm.sectionEleven
              .legalGuardianType
          }
          name="legal_guardian_type"
          options={legalGuardianOptions}
          size="md"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="legal_guardian_house_number"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={translations.patientForms.drvGeneralForm.sectionEleven.street}
          name="legal_guardian_house_number"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="legal_guardian_zip_code"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={translations.patientForms.drvGeneralForm.sectionEleven.zipCode}
          name="legal_guardian_zip_code"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="legal_guardian_town"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={translations.patientForms.drvGeneralForm.sectionEleven.city}
          name="legal_guardian_town"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="legal_guardian_phone"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={translations.patientForms.drvGeneralForm.sectionEleven.phone}
          name="legal_guardian_phone"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="legal_guardian_email"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={translations.patientForms.drvGeneralForm.sectionEleven.email}
          name="legal_guardian_email"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
        fieldName="consent_of_the_insured"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledRadioGroup
          label={
            translations.patientForms.drvGeneralForm.sectionEleven
              .lackOfCapacity
          }
          name="consent_of_the_insured"
          options={lackOfCapacityOptions}
          size="md"
        />
      </SourceWrapper>
    </>
  );
};

export const NeedsDisabilityEquipmentField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_GENERAL>
      fieldName="needs_disability_equipment"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledInput
        label={
          translations.patientForms.drvGeneralForm.sectionTwelev.descriptionAids
        }
        name="needs_disability_equipment"
      />
    </SourceWrapper>
  );
};

export const SendDocumentsPerEmailDescriptionField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const sendDocumentsPerEmail = useWatch({
    name: "send_documents_per_email",
  });

  if (!sendDocumentsPerEmail) {
    return null;
  }

  return (
    <ControlledInput
      label={
        translations.patientForms.drvGeneralForm.sectionThirteen
          .germanMailAddress
      }
      maxLength={SEND_DOCUMENTS_CHARACTER_LIMIT}
      name="send_documents_per_email_description"
    />
  );
};

export const RequestedRehabServiceField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const requestedRehabService = useWatch({
    name: "requested_rehab_service",
  });

  useEffect(() => {
    if (requestedRehabService !== REQUESTED_REHAB_SERVICE.INSURED) {
      setValue("requested_rehab_service_type_insured", undefined);
    }
    if (requestedRehabService !== REQUESTED_REHAB_SERVICE.RELATIVES) {
      setValue("requested_rehab_service_type_relatives", undefined);
    }
  }, [requestedRehabService, setValue]);

  const options = [
    {
      label:
        translations.patientForms.drvGeneralForm.sectionOne.rehabForInsured,
      subForm: () => (
        <RequestedRehabServiceTypeInsuredField
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: REQUESTED_REHAB_SERVICE.INSURED,
    },
    {
      label:
        translations.patientForms.drvGeneralForm.sectionOne.oncologicalRehab,
      subForm: () => (
        <RequestedRehabServiceTypeRelativesField
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: REQUESTED_REHAB_SERVICE.RELATIVES,
    },
  ];

  return (
    <ControlledRadioGroup
      aria-label={translations.patientForms.drvGeneralForm.sectionOne.title}
      className="w-full"
      name="requested_rehab_service"
      options={options}
      size="md"
    />
  );
};

export const PatientUnableToWorkField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const unableToWork = useWatch({
    name: "patient_unable_to_work_before_hospitalization",
  });

  useEffect(() => {
    if (unableToWork !== PREDICAMENT_STATE_YES) {
      setValue("patient_unable_to_work_before_hospitalization_date", undefined);
    }
  }, [unableToWork, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <PatientUnableToWorkDateField
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={
        translations.patientForms.drvGeneralForm.sectionSix.subtitleHospitalStay
      }
      name="patient_unable_to_work_before_hospitalization"
      options={options}
      size="md"
    />
  );
};

export const ContributionsToForeignInsuranceInfoField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const contributionsInfo = useWatch({
    name: "contributions_to_foreign_insurance_info",
  });

  useEffect(() => {
    if (contributionsInfo !== PREDICAMENT_STATE_YES) {
      setValue("contributions_to_foreign_insurance_info_state", "");
      setValue("contributions_to_foreign_insurance_info_from_to", "");
    }
  }, [contributionsInfo, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <ContributionsToForeignInsuranceInfoFields
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      aria-label={
        translations.patientForms.drvGeneralForm.sectionEight
          .contributionsAbroad
      }
      className="w-full"
      name="contributions_to_foreign_insurance_info"
      options={options}
      size="md"
    />
  );
};

export const UnemploymentBenefitField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const unemploymentBenefit = useWatch({
    name: "unemployment_benefit_type_2",
  });

  useEffect(() => {
    if (unemploymentBenefit !== PREDICAMENT_STATE_YES) {
      setValue("jobcenter_name", "");
    }
  }, [unemploymentBenefit, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <JobcenterNameField
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={translations.patientForms.drvGeneralForm.sectionNine.subtitle}
      name="unemployment_benefit_type_2"
      options={options}
      size="md"
    />
  );
};

export const RetirementPensionReceivedApplicableField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const pensionReceived = useWatch({
    name: "retirement_pension_received_applicable",
  });

  useEffect(() => {
    if (pensionReceived !== PREDICAMENT_STATE_YES) {
      setValue("retirement_pension_received", "");
    }
  }, [pensionReceived, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <RetirementPensionReceivedField
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={
        translations.patientForms.drvGeneralForm.sectionTen.penisonApplication
      }
      name="retirement_pension_received_applicable"
      options={options}
      size="md"
    />
  );
};

export const BeforeRetirementPensionApplicableField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const beforeRetirement = useWatch({
    name: "before_retirement_pension_applicable",
  });

  useEffect(() => {
    if (beforeRetirement !== PREDICAMENT_STATE_YES) {
      setValue("before_retirement_pension", "");
    }
  }, [beforeRetirement, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <BeforeRetirementPensionField
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={translations.patientForms.drvGeneralForm.sectionTen.otherBenefits}
      name="before_retirement_pension_applicable"
      options={options}
      size="md"
    />
  );
};

export const HealthDamagePresentField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const healthDamage = useWatch({ name: "health_damage_present" });

  useEffect(() => {
    if (healthDamage !== PREDICAMENT_STATE_YES) {
      setValue("health_damage_origin", "");
      setValue("health_damage_file_number", "");
      setValue("health_damages_description", "");
    }
  }, [healthDamage, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <HealthDamageFields
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      aria-label={
        translations.patientForms.drvGeneralForm.sectionTen.workRelatedAccident
      }
      className="w-full"
      name="health_damage_present"
      options={options}
      size="md"
    />
  );
};

export const ApplicationSubmittedApplicableField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const applicationSubmitted = useWatch({
    name: "application_submitted_applicable",
  });

  useEffect(() => {
    if (applicationSubmitted !== PREDICAMENT_STATE_YES) {
      setValue("application_submitted", "");
    }
  }, [applicationSubmitted, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <ApplicationSubmittedField
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={
        translations.patientForms.drvGeneralForm.sectionTen.submittedApplication
      }
      name="application_submitted_applicable"
      options={options}
      size="md"
    />
  );
};

export const ClaimForDamagesApprovedField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const claimApproved = useWatch({ name: "claim_for_damages_approved" });

  useEffect(() => {
    if (claimApproved !== PREDICAMENT_STATE_YES) {
      setValue("claim_for_damages_origin", "");
      setValue("claim_for_damages_file_number", "");
      setValue("claim_for_damages_description", undefined);
    }
  }, [claimApproved, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <ClaimForDamagesFields
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={translations.patientForms.drvGeneralForm.sectionTen.damageClaims}
      name="claim_for_damages_approved"
      options={options}
      size="md"
    />
  );
};

export const ThirdPartyRequestField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const thirdPartyRequest = useWatch({ name: "third_party_request" });

  useEffect(() => {
    if (thirdPartyRequest !== PREDICAMENT_STATE_YES) {
      setValue("contact_guardian", "");
      setValue("legal_guardian_type", undefined);
      setValue("legal_guardian_house_number", "");
      setValue("legal_guardian_zip_code", "");
      setValue("legal_guardian_town", "");
      setValue("legal_guardian_phone", "");
      setValue("legal_guardian_email", "");
      setValue("consent_of_the_insured", undefined);
    }
  }, [thirdPartyRequest, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <ThirdPartyRequestFields
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={translations.patientForms.drvGeneralForm.sectionEleven.subtitle}
      name="third_party_request"
      options={options}
      size="md"
    />
  );
};

export const NeedsDisabilityEquipmentApplicableField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const needsEquipment = useWatch({
    name: "needs_disability_equipment_applicable",
  });

  useEffect(() => {
    if (needsEquipment !== PREDICAMENT_STATE_YES) {
      setValue("needs_disability_equipment", "");
    }
  }, [needsEquipment, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <NeedsDisabilityEquipmentField
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={translations.patientForms.drvGeneralForm.sectionTwelev.aids}
      name="needs_disability_equipment_applicable"
      options={options}
      size="md"
    />
  );
};

export const SendDocumentsPerEmailField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_GENERAL>) => {
  const { setValue } = useFormContext<DRVGeneralFormData>();
  const sendDocuments = useWatch({ name: "send_documents_per_email" });

  useEffect(() => {
    if (!sendDocuments) {
      setValue("send_documents_per_email_description", "");
    }
  }, [sendDocuments, setValue]);

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.drvGeneralForm.sectionThirteen.confirmation
      }
      name="send_documents_per_email"
      size="md"
    />
  );
};
